<template>
  <div :class="[$options.name]" :data-e-ga-id="uniqueId">
    <button :class="[type, closeTriggerClassName, { loading: loading }, { active: active }]" @click="handleClick">
      <span v-if="loading">
        <font-awesome-icon icon="fa-regular fa-spinner-third" :class="`${$options.name}__icon`" spin />
      </span>
      <div v-else :class="`tw:flex tw:gap-4 tw:items-center tw:justify-between ${closeTriggerClassName}`">
        <div class="tw:flex" :class="{ 'tw:flex-col tw:items-start': desc }">
          <div class="tw:flex tw:gap-4 tw:items-center">
            <font-awesome-icon v-if="icon" :icon="icon" style="width: 20px" />
            <span class="tw:body-2 tw:text-black" :class="closeTriggerClassName">{{ text }}</span>
          </div>
          <span v-if="desc" class="tw:caption">{{ desc }}</span>
        </div>
        <font-awesome-icon v-if="showExternalLinkIcon" icon="fa-light fa-arrow-up-right-from-square" />
        <slot v-else name="left-item" />
      </div>
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

export default defineComponent({
  name: "EdroneDropdownItem",
  props: {
    /**
     * Icon name from font-awesome
     */
    icon: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Text to display
     */
    text: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * desc to display
     */
    desc: {
      type: String,
      default: "",
    },
    /**
     * Type of button
     */
    type: {
      type: String as PropType<"default" | "link" | "disabled" | "danger">,
      required: false,
      default: "default",
    },
    /**
     * If true, sets button to active state
     */
    active: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If true, shows loading spinner
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If true, adds an showExternalLinkIcon icon to the right of the button
     */
    showExternalLinkIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Class name that trigger closing dropdown after clicking element that has this class name. When this prop is not provided, then clicking anywhere inside the dropdown will close it.
     */
    closeTriggerClassName: {
      type: String,
      required: false,
      default: "",
    },
    uniqueId: {
      type: String,
      required: true,
    },
  },
  emits: ["click"],
  setup(props, ctx) {
    function handleClick() {
      if (props.type === "disabled") return;
      ctx.emit("click");
      gtagUniversalSend({ component_id: props.uniqueId });
    }
    return { handleClick };
  },
});
</script>

<style scoped>
@reference "vue-app/assets/scss/main.css";

.item {
  display: block !important;
  align-items: flex-start !important;
  margin: 0 !important;
}

.EdroneDropdownItem button {
  min-width: 160px;
  width: 100%;
  padding: 12px !important;
  border: 0;
  cursor: pointer;
  text-align: left !important;
  transition: background-color 0.3s ease-in-out;
}

.EdroneDropdownItem button {
  @apply tw:text-black tw:bg-white;
}

.EdroneDropdownItem button.link span,
.EdroneDropdownItem button.link svg {
  @apply tw:text-blue-500;
}

.EdroneDropdownItem button:hover {
  @apply tw:bg-default-000;
}

.EdroneDropdownItem button.disabled {
  cursor: not-allowed;
}

.EdroneDropdownItem button.disabled span,
.EdroneDropdownItem button.disabled svg {
  @apply tw:text-default-500;
}

.EdroneDropdownItem button.active {
  @apply tw:bg-default-100;
}

.EdroneDropdownItem button.danger span,
.EdroneDropdownItem button.danger svg {
  @apply tw:text-red-500;
}

.EdroneDropdownItem button.danger:hover {
  @apply tw:bg-red-000;
}

.EdroneDropdownItem button.loading {
  cursor: wait;
  display: flex;
  justify-content: center;
}
</style>
